import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";

const PremiumIndexChart = ({
    chartId,
    chartClass,
    chartData,
    chartLabels
}) => {
    useLayoutEffect(() => {
        if (!chartData || !Array.isArray(chartData) || chartData.length === 0) return;

        // Transform data: add a 'label' property for the category axis
        const data = chartData.map(row => ({
            ...row,
            label: row.company || row.brand || row.manufacturer || row.category || ""
        }));

        let root = am5.Root.new(chartId);
        root.setThemes([am5themes_Animated.new(root)]);

        // Chart
        let chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: true,
                panY: true,
                layout: root.verticalLayout
            })
        );

        // Category Axis (Y)
        let yAxis = chart.yAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: "label",
                renderer: am5xy.AxisRendererY.new(root, {
                    inversed: true,
                    minGridDistance: 25,
                    // stroke: am5.color(0x757575),
                    stroke: am5.color(0x707070),      // axis line color
                    strokeWidth: 1,
                    strokeOpacity: 1,
                    cellStartLocation: 0.1,
                    cellEndLocation: 0.9
                    // strokeWidth: 0.3,
                    // strokeOpacity: 1,
                    // cellStartLocation: 0.1,
                    // cellEndLocation: 0.9
                })
            })
        );
        yAxis.data.setAll(data);
        // HIDE Y GRID
        yAxis.get("renderer").grid.template.setAll({
            visible: false
        });
        // LABEL STYLING - MATCH DESIGN
        yAxis.get("renderer").labels.template.setAll({
            fontFamily: "Ubuntu, sans-serif",
            fontWeight: "400",
            fontSize: 12,
            lineHeight: 1,
            letterSpacing: 0,
            // fill: am5.color(0x667085),
            fill: am5.color(0x757575),
            oversizedBehavior: "truncate",
            maxWidth: 180
        });
        // // Value Axis (X)
        // let xAxis = chart.xAxes.push(
        //     am5xy.ValueAxis.new(root, {
        //         renderer: am5xy.AxisRendererX.new(root, {}),
        //         min: 0,
        //         extraMax: 0.1,
        //         numberFormat: "#.##'%'",
        //         fill: "#757575",
        //         stroke: am5.color(0x757575),
        //         strokeWidth: 0.3,
        //         strokeOpacity: 1,
        //         cellStartLocation: 0.1,
        //         cellEndLocation: 0.9
        //     })
        // );

        // Value Axis (X)
        let xAxis = chart.xAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {
                    stroke: am5.color(0x707070),      // axis line color
                    strokeWidth: 1,
                    strokeOpacity: 1,
                    cellStartLocation: 0.1,
                    cellEndLocation: 0.9
                }),
                min: 0,
                extraMax: 0.1,
                numberFormat: "#.##'%'",
            })
        );

        // HIDE X GRID
        xAxis.get("renderer").grid.template.setAll({
            visible: false
        });

        // LABEL STYLING
        xAxis.get("renderer").labels.template.setAll({
            fontFamily: "Ubuntu, sans-serif",
            fontWeight: "400",
            fontSize: 12,
            lineHeight: 1,
            letterSpacing: 0,
            fill: am5.color(0x757575),         // label color
            oversizedBehavior: "truncate",
            maxWidth: 180
        });

        // // SHOW X BASELINE
        // xAxis.get("renderer").axis.setAll({
        //     strokeOpacity: 1,
        //     strokeWidth: 3,
        //     stroke: am5.color(0x000000)
        // });

        // Colors for each metric
        const colors = ["#1976d2", "#64b5f6", "#90caf9"];

        // Save all series for tooltip lookup
        const allSeries = [];

        // Add series for each metric (clustered bars)
        chartLabels.forEach((label, idx) => {
            // First two series are percent, third is value
            const isPercent = idx < 2;
            let series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: label.label,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueXField: label.value,
                    categoryYField: "label",
                    fill: am5.color(colors[idx % colors.length]),
                    stroke: am5.color(colors[idx % colors.length]),
                    clustered: true
                })
            );

            // Bar style: reduce bar height for more categories
            series.columns.template.setAll({
                height: am5.percent(40),
                fillOpacity: 0.9,
                strokeOpacity: 1,
                cornerRadiusTR: 5,
                cornerRadiusBR: 5
            });

            // --- Tooltip: dark background, white text, fontSize 12, fontFamily cursive, unified ---
            const styledTooltip = am5.Tooltip.new(root, {
                pointerOrientation: "horizontal",
                getFillFromSprite: false,
                getStrokeFromSprite: false,
                autoTextColor: false,
            });
            styledTooltip.label.setAll({
                fill: am5.color(0xffffff),
                fontSize: 12,
                maxWidth: 300,
                oversizedBehavior: "wrap",
                fontFamily: "Ubuntu",
                fontWeight: "300"
            });
            styledTooltip.get("background").setAll({
                fill: am5.color(0x213345),
                fillOpacity: 0.85,
                cornerRadius: 5,
                stroke: am5.color(0x213345)
            });

            // Set tooltip on columns template for per-bar tooltips
            series.columns.template.set("tooltip", styledTooltip);

            allSeries.push({ series, label, isPercent, color: colors[idx % colors.length] });

            // Value label outside bar
            series.bullets.push(() =>
                am5.Bullet.new(root, {
                    locationX: 1,
                    sprite: am5.Label.new(root, {
                        text: isPercent ? "{valueX.formatNumber('#.##')}%" : "{valueX.formatNumber('#.##')}",
                        fill: am5.color("#213345"),
                        centerY: am5.p50,
                        centerX: am5.p0,
                        populateText: true,
                        fontSize: 12,
                        fontWeight: "bold",
                        dx: 8
                    })
                })
            );

            series.data.setAll(data);
        });

        // Now combine all hover values into one tooltip
        allSeries.forEach((item, idx) => {
            item.series.columns.template.adapters.add("tooltipText", (text, target) => {
                const ctx = target.dataItem?.dataContext;
                if (!ctx) return "";

                // Compose tooltip for ALL series for this label/category
                let output = `[bold]${ctx.label}[/]\n`;
                allSeries.forEach(s => {
                    const val = ctx[s.label.value];
                    if (val !== undefined && val !== null && val !== "") {
                        output += `[font-size:12px][${s.color}]●[/] [bold]${s.label.label}: [bold]${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${s.isPercent ? "%" : ""}[/]\n`;
                    }
                });
                return output.trim();
            });
        });

        // Legend
        // let legend = chart.children.push(
        //     am5.Legend.new(root, {
        //         centerX: am5.percent(50),
        //         x: am5.percent(50),
        //         marginTop: 10,
        //         marginBottom: 10
        //     })
        // );
        // legend.data.setAll(chart.series.values);
        let legend = chart.children.push(
            am5.Legend.new(root, {
                centerX: am5.percent(50),
                x: am5.percent(50),
                // marginTop: 10,
                // marginBottom: 10
                marginTop: 10,          // Reduce top margin
                marginBottom: 0,       // Reduce bottom margin
                paddingTop: 2,         // Add small padding if needed
                paddingBottom: 2,
                // maxHeight: 40,
                // height: 40,
            })
        );
        legend.data.setAll(chart.series.values);

        legend.labels.template.setAll({
            fontFamily: "Ubuntu, sans-serif",
            fontSize: 12,
            fill: am5.color(0x213345),
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 5,
            paddingRight: 5,
        });

        legend.markers.template.setAll({
            width: 12,
            height: 12,
            strokeWidth: 2,
            fillOpacity: 1,
            strokeOpacity: 1,
            centerY: am5.p50,
            centerX: am5.p50,
        });

        legend.itemContainers.template.setAll({
            layout: root.horizontalLayout,
            alignItems: "center",
            justifyContent: "center",
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 5,
            paddingRight: 5,
        });


        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [chartId, chartData, chartLabels]);

    return (
        <div id={chartId} className={chartClass} style={{ width: "100%", height: "450px", minHeight: "450px" }} />
    );
};

PremiumIndexChart.propTypes = {
    chartId: PropTypes.string.isRequired,
    chartClass: PropTypes.string,
    chartData: PropTypes.array.isRequired,
    chartLabels: PropTypes.array.isRequired
};

export default PremiumIndexChart;